import { Dispatch, ReactNode, SetStateAction } from "react";
import { cn } from "@tellescope/design-system";
import { useBlockContent } from "./useBlockContent";
import { BlockExpandToggle } from "./BlockExpandToggle";
import { BlockScrollContainerProvider } from "./BlockScrollContainerContext";
import { FLOATING_THUMB_CLASS, SCROLL_SURFACE_CLASS, scrollSurfaceStyle } from "@tellescope/react-hooks";

/**
 * How a block's content area is sized, and what affordances follow from that.
 *
 * - `capped` — the stacked blocks list. The caller supplies a max-height, and content overflowing it
 *   drives the expand toggle and the bottom fade ("there is more, click to expand").
 * - `fill` — the single-block and fullpage-takeover views. The content area bounds itself to the
 *   parent's height and scrolls inside it, with only the floating scrollbar. There is no cap to
 *   expand past, so no toggle and no fade; `expanded`/`onToggleExpanded` are ignored.
 */
export type BlockContentLayout = "capped" | "fill";

export interface BlockContentProps {
  editMode: boolean;
  setEditMode?: Dispatch<SetStateAction<boolean>>;
  className?: string;
  children: ReactNode;
  expanded?: boolean;
  onToggleExpanded?: () => void;
  layout?: BlockContentLayout;
}

export const BlockContent = ({
  editMode,
  setEditMode,
  className,
  children,
  expanded = false,
  onToggleExpanded,
  layout = "capped",
}: BlockContentProps) => {
  const capped = layout === "capped";
  const { isOverflowing, scrollRef, scrollElement, thumbRef, handleScroll } = useBlockContent({
    measureOverflow: capped,
  });

  const contentClassName = cn(
    "mt-2 w-full rounded-sm py-2 overflow-y-auto",
    SCROLL_SURFACE_CLASS,
    capped ? "transition-[max-height,background-color] duration-300 ease-in-out" : "transition-colors",
    !capped && "flex-1 min-h-0",
    !editMode && !!setEditMode && "hover:bg-secondary p-2",
    className,
    capped && expanded && "max-h-[50000px]"
  );

  const showToggle = capped && !!onToggleExpanded && (isOverflowing || expanded);
  const contentStyle = scrollSurfaceStyle({ faded: capped && isOverflowing && !expanded });

  return (
    <div className={cn("relative w-full flex flex-col", !capped && "flex-1 min-h-0")}>
      {editMode || !setEditMode ? (
        <div ref={scrollRef} className={contentClassName} style={contentStyle} onScroll={handleScroll}>
          <BlockScrollContainerProvider key="block-scroll-container-provider" scrollElement={scrollElement}>
            {children}
          </BlockScrollContainerProvider>
        </div>
      ) : (
        <button
          ref={scrollRef}
          className={contentClassName}
          style={contentStyle}
          onScroll={handleScroll}
          onClick={() => setEditMode(true)}
        >
          <BlockScrollContainerProvider key="block-scroll-container-provider" scrollElement={scrollElement}>
            {children}
          </BlockScrollContainerProvider>
        </button>
      )}
      <div ref={thumbRef} className={FLOATING_THUMB_CLASS} />
      {showToggle && <BlockExpandToggle expanded={expanded} onToggle={onToggleExpanded} />}
    </div>
  );
};
